import React, { useEffect, useState } from 'react';
import api from '../api';
import { decodeJwt } from '../auth';

function valueOf(field) {
  if (field === null || field === undefined) return '';
  if (typeof field === 'object' && !Array.isArray(field)) {
    return field.S ?? field.N ?? '';
  }
  return field;
}

function callFromPlay(play) {
  const formation = valueOf(play.formation).trim();
  const motion = valueOf(play.motion).trim();
  const action = valueOf(play.action).trim();
  const motionPart = motion && !/^no\s+motion$/i.test(motion) ? motion : '';
  return [formation, motionPart, action].filter(Boolean).join(' ');
}

export default function PlayList() {
  const [plays, setPlays] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlays = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Not logged in');
          return;
        }

        const decoded = decodeJwt(token);
        const team = decoded?.teamId;
        const { data } = await api.get(`/api/plays/all/${team || 'anything'}`);
        setPlays(data.plays || []);
      } catch (err) {
        setError('Could not load plays.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlays();
  }, []);

  return (
    <section className="surface-card">
      <div className="flex flex-col gap-3 border-b border-stone-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow">View Plays</p>
          <h2 className="mt-3 text-4xl font-semibold text-brand-black">Saved plays</h2>
          <p className="mt-3 max-w-3xl text-lg leading-8 text-stone-700">
            This is the team play library for staff review, cleanup, and weekly planning.
          </p>
        </div>
        <div className="rounded-lg bg-brand-offwhite px-4 py-3 text-base text-stone-700">
          {loading ? 'Loading plays...' : `${plays.length} plays`}
        </div>
      </div>

      {error && (
        <p className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
          {error}
        </p>
      )}

      {!error && loading && (
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-32 animate-pulse rounded-lg bg-brand-offwhite" />
          ))}
        </div>
      )}

      {!error && !loading && plays.length === 0 && (
        <div className="mt-8 rounded-lg border-2 border-dashed border-stone-300 bg-brand-offwhite px-6 py-10 text-center">
          <h3 className="text-2xl font-semibold text-brand-black">No plays saved yet.</h3>
          <p className="mt-3 text-base text-stone-700">
            Add a play first so the staff has a library to review and the mobile app has calls to
            work from.
          </p>
        </div>
      )}

      {!error && !loading && plays.length > 0 && (
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          {plays.map((p) => (
            <article key={valueOf(p.playId)} className="rounded-lg border-2 border-stone-200 bg-brand-offwhite px-5 py-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-orange">
                    {valueOf(p.type) || 'Play'}
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold text-brand-black">{callFromPlay(p) || 'Unnamed play'}</h3>
                  {valueOf(p.nickname) && (
                    <p className="mt-2 text-base text-stone-700">Nickname: {valueOf(p.nickname)}</p>
                  )}
                </div>
                <span className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-stone-700">
                  {valueOf(p.personnel) || 'No personnel'}
                </span>
              </div>

              <dl className="mt-5 grid gap-4 text-base text-stone-700 sm:grid-cols-2">
                <div>
                  <dt className="form-label">Formation</dt>
                  <dd className="mt-1">{valueOf(p.formation) || 'Not listed'}</dd>
                </div>
                <div>
                  <dt className="form-label">Motion</dt>
                  <dd className="mt-1">{valueOf(p.motion) || 'Not listed'}</dd>
                </div>
                <div>
                  <dt className="form-label">Action</dt>
                  <dd className="mt-1">{valueOf(p.action) || 'Not listed'}</dd>
                </div>
                <div>
                  <dt className="form-label">Point of attack</dt>
                  <dd className="mt-1">{valueOf(p.pointOfAttack) || 'Not listed'}</dd>
                </div>
                <div>
                  <dt className="form-label">Best situation</dt>
                  <dd className="mt-1">{valueOf(p.situation) || 'Not listed'}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
